import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Customer-side transient state for an agent's "please share a screenshot" request. Same
 * removable-seam shape as agentTypingSlice: populated only by the live SignalR push
 * (`ReceiveScreenshotRequest`), never persisted, cleared when the customer sends or dismisses.
 */
export interface ScreenshotRequestEntry {
    /** Incremented per request so a repeated request re-surfaces a prompt the customer dismissed. */
    ping: number;
}

export interface ScreenshotRequestState {
    // Keyed by chatId so a request for one chat can't leak into another.
    requests: Record<string, ScreenshotRequestEntry>;
}

const initialState: ScreenshotRequestState = {
    requests: {},
};

export const screenshotRequestSlice = createSlice({
    name: 'screenshotRequest',
    initialState,
    reducers: {
        setScreenshotRequest: (state, action: PayloadAction<{ chatId: string }>) => {
            const prev = state.requests[action.payload.chatId] as ScreenshotRequestEntry | undefined;
            state.requests[action.payload.chatId] = { ping: (prev?.ping ?? 0) + 1 };
        },
        clearScreenshotRequest: (state, action: PayloadAction<{ chatId: string }>) => {
            // Rebuild without the key rather than `delete` on a computed index
            // (@typescript-eslint/no-dynamic-delete).
            const next: Record<string, ScreenshotRequestEntry> = {};
            for (const id of Object.keys(state.requests)) {
                if (id !== action.payload.chatId) {
                    next[id] = state.requests[id];
                }
            }
            state.requests = next;
        },
    },
});

export const { setScreenshotRequest, clearScreenshotRequest } = screenshotRequestSlice.actions;

export default screenshotRequestSlice.reducer;
